import { useMemo } from 'react';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { formatNumberWithTooltip } from 'components/tooltip-number';
import { formatTitleRiskValue } from 'pages/crm-tracts-subtracts/utils';
import {
  subtractCoverageSuffix,
  tractRowCoverageLabel,
} from 'utils/subtract-coverage';
import {
  DECIMAL_VARIABLE,
  EMPTY,
  linkCellRenderer,
  NESTED_TABLE_MULTISELECT_CONFIG,
} from './utils';
import { tagsTextColumn } from 'lib/asset-aliases';
import { useChildHighlightSelection } from './hooks';
import type { SlotAllocationRow, SlotRow } from './types';
import styles from './slot-expanded-row.module.scss';

type TractAllocationTableRow = SlotAllocationRow & Record<string, unknown>;

type TractAllocationColumnKey = keyof SlotAllocationRow & string;

/**
 * Plain-text tract-allocation column rendered by DataTable's `meta.type`.
 *
 * @param {string} accessorKey - The `SlotAllocationRow` field.
 * @param {string} header - The column header label.
 * @param {number} size - The column width in px.
 * @return {object} The DataTable column definition.
 */
function text(
  accessorKey: TractAllocationColumnKey,
  header: string,
  size: number
): DataTableColumnDef<TractAllocationTableRow> {
  return { accessorKey, header, size, meta: { type: 'text' } };
}

/**
 * Tract-allocation columns for an expanded Slot row — the tracts the slot is
 * allocated across (the mock's "tract-allocation children",
 * `CHILD_COLUMNS_FOR_SLOTS`). Matches the mock's column set, headers, and order
 * exactly. The rows are `TractNRI`-shaped (the adapter renames the BE tract child
 * via the slot renames), so the tract link reads from `crmTractSalesforceId` and
 * NRI/WI from `totalSlotNri`/`totalSlotWi` — surfaced under the mock's "UAT"
 * labels (the value is the allocated-per-tract total, not a subtract UAT figure;
 * no formula popup — these tract children carry no `nriCalculationId`). Several
 * columns (CRM Account, Depth Range, Survey Area, Quarter Call, Instrument Type,
 * Allocation Method, Net Acreage Units, Title Quality/Risk) render blank until the
 * BE projects them onto the slot's tract children — see the deferred ledger.
 */
const TRACT_ALLOCATION_COLUMNS: DataTableColumnDef<TractAllocationTableRow>[] =
  [
    {
      accessorKey: 'crmTract',
      header: 'CRM Tract',
      size: 200,
      // A Tract the package holds in part says how much of it the package holds.
      cell: (info) => {
        const { crmTractSalesforceId } = info.row.original;
        const href = crmTractSalesforceId
          ? `/crm/tracts_and_subtracts/${encodeURIComponent(
              crmTractSalesforceId
            )}/edit`
          : undefined;
        const held = tractRowCoverageLabel(info.row.original);
        return (
          <>
            {linkCellRenderer(info.getValue<string>(), href)}
            {subtractCoverageSuffix(held)}
          </>
        );
      },
    },
    {
      accessorKey: 'crmAccount',
      header: 'CRM Account',
      size: 180,
      cell: (info) =>
        linkCellRenderer(
          info.getValue<string>(),
          info.row.original.crmAccountHref
        ),
    },
    tagsTextColumn<TractAllocationTableRow>({
      accessorKey: 'crmTractTags',
      header: 'Asset Tags',
      size: 180,
    }),
    text('depthRange', 'Depth Range', 140),
    text('surveyArea', 'Survey Area', 120),
    text('quarterCall', 'Quarter Call', 120),
    text('instrumentType', 'Instrument Type', 140),
    text('allocationMethod', 'Allocation Method', 160),
    text('netAcreageUnits', 'Net Acreage Units', 150),
    {
      accessorKey: 'totalSlotNri',
      header: 'UAT NRI',
      size: 130,
      // `meta.type` drives right-alignment only (a custom `cell` suppresses its
      // formatter); the empty indicator is passed explicitly so a missing value
      // shows `—` rather than a fabricated `0.00000000`.
      meta: { type: 'decimal' },
      cell: (info) =>
        formatNumberWithTooltip(info.getValue<number | null>(), 8, EMPTY),
    },
    {
      accessorKey: 'totalSlotWi',
      header: 'UAT WI',
      size: 130,
      meta: { type: 'decimal' },
      cell: (info) =>
        formatNumberWithTooltip(info.getValue<number | null>(), 8, EMPTY),
    },
    {
      accessorKey: 'tractNetAcres',
      header: 'Subtract Net Acres',
      size: 160,
      meta: DECIMAL_VARIABLE,
    },
    {
      accessorKey: 'tractGrossAcres',
      header: 'Subtract Gross Acres',
      size: 170,
      meta: DECIMAL_VARIABLE,
    },
    {
      accessorKey: 'titleQuality',
      header: 'Title Quality',
      size: 130,
      cell: (info) =>
        formatTitleRiskValue(info.getValue<string | null>(), EMPTY),
    },
    {
      accessorKey: 'titleRiskFactor',
      header: 'Title Risk Factor',
      size: 150,
      cell: (info) =>
        formatTitleRiskValue(info.getValue<string | null>(), EMPTY),
    },
  ];

interface SlotExpandedRowProps {
  row: SlotRow;
}

/**
 * Expanded content for a Slot row: the tract-allocation children (the tracts the
 * slot is allocated across), per mock UI v2. The new BE `slots` view emits
 * tract-grain `children`, which the adapter maps onto `row.tractAllocations`.
 * Renders nothing when the slot spans no tracts. (The slot's subtract breakdown
 * lives on the main-row NRI/WI rollup popup, not here.)
 *
 * @param {SlotExpandedRowProps} props - The parent slot row supplying the tract allocations.
 * @return {JSX.Element | null} The tract-allocation table, or null when empty.
 */
export function SlotExpandedRow({ row }: SlotExpandedRowProps) {
  const tractAllocations = useMemo(
    () => (row.tractAllocations ?? []) as TractAllocationTableRow[],
    [row.tractAllocations]
  );

  // Tract-Allocation checkboxes drive the map highlight: each checked tract child
  // contributes its tract polygon to the pinned slot's scope (the well + unit are
  // the slot's own geometry and always show). Toggling auto-pins this slot row.
  const tractChildIds = useMemo(
    () => tractAllocations.map((allocation) => String(allocation.id)),
    [tractAllocations]
  );
  const tractSelection = useChildHighlightSelection(
    row as unknown as Record<string, unknown>,
    tractChildIds
  );

  if (tractAllocations.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.nestedTable}>
        <DataTable<TractAllocationTableRow>
          data={tractAllocations}
          columns={TRACT_ALLOCATION_COLUMNS}
          getRowId={(allocation) => String(allocation.id)}
          config={NESTED_TABLE_MULTISELECT_CONFIG}
          controlledState={{ selection: tractSelection.selection }}
          onSelectionChange={tractSelection.onSelectionChange}
        />
      </div>
    </div>
  );
}
